import { Tenant } from './model';
import { LoadingStateType, SetLoadingStateType } from '../LoadingStateType';
import create from 'zustand';
import { useMutation, UseMutationResult, useQuery, useQueryClient, UseQueryResult } from 'react-query';
import { useEffect, useRef } from 'react';
import { restClient } from '../../../App';
import { convertTenantDTO } from './tenantConverter';
import { ApiException } from '../interface/ApiException';
import { ApiError } from '../interface/ApiError';
import { TenantUpdateRequest } from '../interface/apiModels';

export const TENANT_KEY = 'tenant';

const useTenant = (): UseQueryResult<Tenant | undefined, ApiException<ApiError>> => {
  return useQuery<Tenant, ApiException<ApiError>, Tenant>(TENANT_KEY, () => fetchTenant(), {});
};

const fetchTenant = async (): Promise<Tenant> => {
  const response = await restClient.getTenant();
  return convertTenantDTO(response);
};

const updateTenant = async (request: TenantUpdateRequest): Promise<void> => {
  await restClient.updateTenant(request);
};

export const useUpdateTenant = (): UseMutationResult<void, ApiException<ApiError>, TenantUpdateRequest> => {
  const queryClient = useQueryClient();
  return useMutation(TENANT_KEY, updateTenant, {
    onSuccess: () => {
      return queryClient.invalidateQueries(TENANT_KEY);
    },
  });
};

type TenantStoreStateType = LoadingStateType &
  SetLoadingStateType & {
    tenant: Tenant;
    setTenant: (newTenant: Tenant) => void;
    cleanup: () => void;
  };

const tenantState = create<TenantStoreStateType>((set) => ({
  tenant: undefined,
  setTenant: (newTenant: Tenant) => set(() => ({ tenant: newTenant })),
  cleanup: () =>
    set(() => {
      return { tenant: undefined };
    }),
  isLoading: false,
  isSuccess: false,
  isError: false,
  error: undefined,
  setLoadingState: (isLoading, isError, isSuccess, error) =>
    set(() => ({
      isLoading,
      isError,
      isSuccess,
      error,
    })),
}));

export const useTenantState = () => {
  const { tenant, setTenant, cleanup, setLoadingState } = tenantState();
  const { data: loadedTenant, isLoading, isError, isSuccess, error } = useTenant();

  const isMounted = useRef(true); // Add a ref to track the mount status

  useEffect(() => {
    setLoadingState(isLoading, isError, !!loadedTenant, error);
    if (loadedTenant) {
      setTenant(loadedTenant);
    }

    return () => {
      isMounted.current = false; // Set to false when unmounted
    };
  }, [loadedTenant, isSuccess, isError, isLoading, error, cleanup, setTenant, setLoadingState]);

  // Add an additional effect to reset the tenant only on complete unmounting
  useEffect(() => {
    if (!isMounted.current) {
      cleanup();
    }
  }, [isMounted]);

  return { tenant, isSuccess, isError, isLoading, error };
};
